/**
 * App-level first-touch UTM attribution store.
 *
 * Why this exists (and not PostHog's native attribution): the canonical
 * PostHog client runs person_profiles:'identified_only' and never calls
 * identify()/$set (see posthog-client.ts), so anonymous events never create
 * a person profile — PostHog's native person-level first-touch properties
 * ($initial_utm_*) are intentionally never persisted. Current-touch UTM is
 * still captured natively on each event by posthog-js. This module supplies
 * the missing FIRST-TOUCH layer at the application level.
 *
 * Behavior:
 * - Consent-gated: nothing is persisted unless isAnalyticsAllowed() is true
 *   (same central resolver GA4/Clarity/PostHog use). No UTM is written to
 *   storage before analytics consent.
 * - First-touch wins: the first consented tagged landing's UTM set is stored
 *   once and NEVER overwritten by a later campaign or replaced during
 *   internal / untagged navigation.
 * - Empty query values are ignored; untagged landings write nothing.
 * - One namespaced record (`pm_first_touch_utm`), not several keys.
 * - Fail-safe storage: mirrors consent-manager's in-memory fallback so a
 *   blocked/unavailable localStorage never throws.
 * - Cleared on consent withdrawal via clearFirstTouchUtm() (wired in
 *   AnalyticsGate alongside disablePostHogCapture()).
 *
 * These values are attached to the contact_form_submitted PostHog event and
 * forwarded through the /api/contact payload for persistence on the lead
 * record, and to the free-check payload for persistence on radar_leads.
 *
 * Storable keys are exactly ATTRIBUTION_KEYS: the five utm_* params plus
 * `placement` (Meta's ad-surface token, see below). Never widen this to any
 * other URL param — this record is not a general query-string store.
 */

import { isAnalyticsAllowed } from '@/lib/consent-manager'

export const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id',
] as const

/**
 * `placement` is NOT a utm_* param, but Meta's ad URL template emits it
 * alongside them (`placement={{placement}}` -> feed / story / reels) and it is
 * the only way to tell those surfaces apart: every Instagram placement shares
 * one utm_source. It is captured under the same consent gate, the same
 * first-touch-wins rule, and the same length bound as the utm_* keys.
 *
 * Kept separate from UTM_KEYS so `/api/contact` and anything else iterating
 * strictly over utm_* is unaffected.
 */
export const ATTRIBUTION_EXTRA_KEYS = ['placement'] as const

export const ATTRIBUTION_KEYS = [
  ...UTM_KEYS,
  ...ATTRIBUTION_EXTRA_KEYS,
] as const

export type UtmKey = (typeof UTM_KEYS)[number]
export type AttributionKey = (typeof ATTRIBUTION_KEYS)[number]
export type FirstTouchUtm = Partial<Record<AttributionKey, string>>

const STORAGE_KEY = 'pm_first_touch_utm'
// Conservative bound so a hostile/oversized query param can never bloat the
// record, the PostHog event, or the persisted lead row.
const MAX_VALUE_LENGTH = 200

// In-memory mirror: if localStorage is unavailable (Safari private mode,
// storage disabled, embedded contexts) the first-touch record still governs
// this session and reads/writes never throw.
let memoryRecord: FirstTouchUtm | null = null

function safeGet(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

function safeSet(value: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, value)
  } catch {
    // Held in memory only for this session.
  }
}

function safeRemove(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // no-op
  }
}

/**
 * Current first-touch record, or null if none has been captured.
 *
 * Consent-aware read (defense against silent consent EXPIRY): if analytics are
 * not currently allowed — an explicit denial, an undecided prior-consent
 * region, or a stored consent record that lapsed past its 1-year expiry with no
 * `consentDenied` event ever firing — this never returns stale attribution. It
 * clears the record (in-memory + localStorage) and returns null, so stale
 * attribution can neither be read, forwarded to `/api/contact`, attached to
 * `contact_form_submitted`, nor block a fresh first-touch after consent is
 * granted again. `captureFirstTouchUtm` only reaches this after its own
 * `isAnalyticsAllowed()` gate, so first-touch-wins is preserved within a
 * consent period.
 */
export function getFirstTouchUtm(): FirstTouchUtm | null {
  if (typeof window === 'undefined') return null
  if (!isAnalyticsAllowed()) {
    clearFirstTouchUtm()
    return null
  }
  const stored = safeGet()
  if (stored) {
    try {
      const parsed = JSON.parse(stored) as FirstTouchUtm
      if (parsed && typeof parsed === 'object') return parsed
    } catch {
      // Corrupted record — fall through to the memory mirror.
    }
  }
  return memoryRecord
}

/**
 * Persist the first non-empty UTM values from the current URL, once.
 *
 * No-ops when: SSR, analytics not allowed (consent), the URL carries no
 * non-empty utm_* param, or a first-touch record already exists (first-touch
 * wins — later campaigns and internal/untagged navigation never overwrite it).
 */
export function captureFirstTouchUtm(
  search: string | URLSearchParams | null | undefined
): void {
  if (typeof window === 'undefined') return
  if (!isAnalyticsAllowed()) return

  // First-touch is immutable once set — bail before parsing.
  const existing = getFirstTouchUtm()
  if (existing && Object.keys(existing).length > 0) return

  let params: URLSearchParams
  try {
    params =
      typeof search === 'string'
        ? new URLSearchParams(search)
        : (search ?? new URLSearchParams())
  } catch {
    return
  }

  const record: FirstTouchUtm = {}
  for (const key of ATTRIBUTION_KEYS) {
    const raw = params.get(key)
    if (raw == null) continue
    const value = raw.trim()
    if (!value) continue // ignore empty query-string values
    record[key] = value.slice(0, MAX_VALUE_LENGTH)
  }

  if (Object.keys(record).length === 0) return // untagged landing

  memoryRecord = record
  safeSet(JSON.stringify(record))
}

/**
 * Remove the first-touch record. Called on consent withdrawal so persisted
 * attribution follows the same cleanup as the rest of the analytics stack.
 */
export function clearFirstTouchUtm(): void {
  if (typeof window === 'undefined') return
  memoryRecord = null
  safeRemove()
}

/** Test-only: reset module state between vitest cases. */
export function _resetFirstTouchUtmForTests(): void {
  memoryRecord = null
}
